import { useCallback, useEffect, useMemo, useState } from 'react'

import { Download } from 'lucide-react'
import { usePapaParse } from 'react-papaparse'

import { cn } from '~/utils/tailwind-utils'

type CsvRowData = (string | null | undefined)[]

function rowsToRecipientsText(rows: CsvRowData[] | unknown): string {
  if (!Array.isArray(rows)) return ''

  return rows
    .map((row) => {
      const cols = Array.isArray(row)
        ? row
        : (row as { data: CsvRowData })?.data
      if (!Array.isArray(cols)) return null

      const address = String(cols[0] ?? '').trim()
      const amount = String(cols[1] ?? '').trim()

      if (!address && !amount) return null
      return `${address}, ${amount}`
    })
    .filter(Boolean)
    .join('\n')
}

type CSVUploadProps = {
  fileInputRef: React.RefObject<HTMLInputElement | null>
  onParse: (text: string) => void
  onError?: (error: string) => void
  children: React.ReactNode
  className?: string
}

export const CSVUpload = ({
  fileInputRef,
  onParse,
  onError,
  children,
  className,
}: CSVUploadProps) => {
  const { readString } = usePapaParse()
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)

  const parseCsvFile = useCallback(
    async (file: File) => {
      const isCsv =
        file.type === 'text/csv' ||
        file.name.toLowerCase().endsWith('.csv') ||
        file.type === 'application/vnd.ms-excel'

      if (!isCsv) {
        onError?.('Upload rejected. Please upload a .csv file.')
        return
      }

      const csvText = await file.text()

      readString(csvText, {
        skipEmptyLines: 'greedy',
        // results is now typed as PapaParseCompleteResult
        complete: (results: { data: CsvRowData[] }) => {
          const text = rowsToRecipientsText(results?.data)

          if (!text) {
            onError?.('Invalid CSV format. Expected: address, amount per row.')
            return
          }

          onParse(text)
        },
        error: (err: Error) => {
          onError?.(err?.message ?? 'Failed to parse CSV')
        },
      })
    },
    [readString, onParse, onError],
  )

  const isFileDrag = useCallback(
    (items?: DataTransferItemList | null) =>
      Array.from(items || []).some((item) => item.kind === 'file'),
    [],
  )

  const dropHandlers = useMemo(
    () => ({
      onDragEnter: (e: React.DragEvent) => {
        if (!isFileDrag(e.dataTransfer?.items)) return
        e.preventDefault()
        setIsDraggingOver(true)
      },
      onDragOver: (e: React.DragEvent) => {
        if (!isFileDrag(e.dataTransfer?.items)) return
        e.preventDefault()
        setIsDraggingOver(true)
      },
      onDragLeave: (e: React.DragEvent) => {
        // dataTransfer can be empty on dragleave; always clear to avoid a stuck state.
        e.preventDefault()
        setIsDraggingOver(false)
      },
      onDrop: async (e: React.DragEvent) => {
        e.preventDefault()
        setIsDraggingOver(false)

        const file = e.dataTransfer.files?.[0]
        if (!file) return

        await parseCsvFile(file)
      },
    }),
    [isFileDrag, parseCsvFile],
  )

  // Track global drag state (anywhere on window)
  useEffect(function useGlobalDragState() {
    let dragCounter = 0

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      dragCounter++
      const isFileDrag = Array.from(e.dataTransfer?.items || []).some(
        (i) => i.kind === 'file',
      )
      if (isFileDrag && dragCounter === 1) {
        setIsDraggingFile(true)
      }
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      dragCounter--
      if (dragCounter === 0) {
        setIsDraggingFile(false)
      }
    }

    const reset = () => {
      dragCounter = 0
      setIsDraggingFile(false)
      setIsDraggingOver(false)
    }

    // Track global drag state
    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragleave', handleDragLeave)

    // Reset drag state when drag operations complete
    window.addEventListener('drop', reset)
    window.addEventListener('dragend', reset)
    window.addEventListener('dragexit', reset)

    return () => {
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('drop', reset)
      window.removeEventListener('dragend', reset)
      window.removeEventListener('dragexit', reset)
    }
  }, [])

  // Prevent browser from opening dropped files outside the drop zone
  useEffect(function usePreventBrowserFileDrop() {
    const preventNav = (e: DragEvent) => {
      e.preventDefault()
    }

    // Prevent browser navigation on file drops
    window.addEventListener('dragover', preventNav)
    window.addEventListener('drop', preventNav)

    return () => {
      window.removeEventListener('dragover', preventNav)
      window.removeEventListener('drop', preventNav)
    }
  }, [])

  return (
    <div className={cn('relative rounded-lg', className)} {...dropHandlers}>
      <div
        className={cn('transition-opacity', {
          'opacity-0': isDraggingFile && isDraggingOver,
        })}
      >
        {children}
      </div>
      {/* Overlay only while dragging so typing/clicking normally still works */}
      <div
        className={cn('pointer-events-none absolute inset-0 rounded-lg', {
          'border-2 border-dashed border-gray-400 bg-gray-500/10 dark:border-gray-500':
            isDraggingFile && isDraggingOver,
        })}
      />

      <div
        className={cn(
          'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          {
            hidden: !isDraggingFile || !isDraggingOver,
          },
        )}
      >
        <Download className="h-8 w-8" />
      </div>

      {/* Hidden file input for click-to-upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv,application/vnd.ms-excel"
        className="hidden"
        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
          // e: React.ChangeEvent<HTMLInputElement> is correctly typed
          const file = e.target.files?.[0]
          if (!file) return

          await parseCsvFile(file)

          // allow re-upload of same file
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }}
      />
    </div>
  )
}
